"use client"

import React from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import { cn } from '../../../lib/utils'
import { Calendar, ChevronLeft, ChevronRight, Clock } from 'lucide-react'
import { Button } from '../../ui/button'
import { Badge } from '../../ui/badge'

interface CalendarEvent {
  id: string
  date: Date
  title: string
  time?: string
  type?: 'primary' | 'secondary' | 'warning' | 'danger' | 'success'
  description?: string
}

interface CalendarWidgetProps {
  id: string
  title: string
  description?: string
  events: CalendarEvent[]
  className?: string
  height?: number
  glassmorphism?: boolean
  compact?: boolean
  interactive?: boolean
  onDateClick?: (date: Date, events: CalendarEvent[]) => void
  onEventClick?: (event: CalendarEvent) => void
}

export function CalendarWidget({
  id,
  title,
  description,
  events,
  className,
  height = 300,
  glassmorphism = false,
  compact = false,
  interactive = true,
  onDateClick,
  onEventClick
}: CalendarWidgetProps) {
  const [currentDate, setCurrentDate] = React.useState(new Date())
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null)
  
  const calendarHeight = compact ? 180 : height

  // Ayın ilk ve son günlerini al
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
  const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
  const firstDayOfWeek = firstDayOfMonth.getDay()
  const daysInMonth = lastDayOfMonth.getDate()

  // Önceki ve sonraki aya git
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))
  }

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))
  }

  // Bugüne git
  const goToToday = () => {
    setCurrentDate(new Date())
    setSelectedDate(new Date())
  }

  // Tarih için event'leri al
  const getEventsForDate = (date: Date) => {
    return events.filter(event => {
      const eventDate = new Date(event.date)
      return eventDate.getDate() === date.getDate() &&
             eventDate.getMonth() === date.getMonth() &&
             eventDate.getFullYear() === date.getFullYear()
    })
  }

  // Event tipi renkleri
  const getEventColor = (type?: CalendarEvent['type']) => {
    switch (type) {
      case 'primary':
        return 'bg-primary'
      case 'secondary':
        return 'bg-secondary'
      case 'warning':
        return 'bg-yellow-500'
      case 'danger':
        return 'bg-destructive'
      case 'success':
        return 'bg-green-500'
      default:
        return 'bg-primary'
    }
  }

  // Ay isimleri
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  // Hafta günleri - compact modda Tuesday için Tu, Thursday için Th kullan
  const weekDays = compact ? ['S', 'M', 'Tu', 'W', 'Th', 'F', 'S'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  // Takvim günlerini oluştur
  const calendarDays = []
  
  // Önceki ayın son günleri
  for (let i = 0; i < firstDayOfWeek; i++) {
    calendarDays.push(null)
  }
  
  // Bu ayın günleri
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(new Date(currentDate.getFullYear(), currentDate.getMonth(), day))
  }

  return (
    <Card className={cn(
      "relative overflow-hidden",
      glassmorphism && "bg-background/60 backdrop-blur-md border-white/10",
      className
    )}>
      {/* Header */}
      <CardHeader className={cn(
        "flex flex-row items-center justify-between space-y-0",
        compact ? "pb-1 pt-2 px-3" : "pb-2"
      )}>
        <div className="flex items-center gap-2">
          <Calendar className={cn(
            "text-muted-foreground",
            compact ? "h-3.5 w-3.5" : "h-4 w-4"
          )} />
          <CardTitle className={cn(
            "font-semibold",
            compact ? "text-xs" : "text-base"
          )}>{title}</CardTitle>
        </div>
        
        {/* Navigation */}
        <div className="flex items-center gap-1">
          {!compact && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={goToToday}
            >
              Today
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "p-0",
              compact ? "h-5 w-5" : "h-7 w-7"
            )}
            onClick={goToPreviousMonth}
          >
            <ChevronLeft className={cn(
              compact ? "h-3 w-3" : "h-4 w-4"
            )} />
          </Button>
          <span className={cn(
            "font-medium min-w-[100px] text-center",
            compact ? "text-[10px]" : "text-sm"
          )}>
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "p-0",
              compact ? "h-5 w-5" : "h-7 w-7"
            )}
            onClick={goToNextMonth}
          >
            <ChevronRight className={cn(
              compact ? "h-3 w-3" : "h-4 w-4"
            )} />
          </Button>
        </div>
      </CardHeader>

      {/* Calendar Content */}
      <CardContent className={cn(
        "p-0",
        compact ? "px-2 pb-2" : "px-4 pb-4"
      )}>
        <div style={{ height: calendarHeight }}>
          {/* Week days header */}
          <div className="grid grid-cols-7 gap-0 mb-1">
            {weekDays.map((day, index) => (
              <div
                key={`weekday-${index}`}
                className={cn(
                  "text-center font-medium text-muted-foreground",
                  compact ? "text-[9px] py-0.5" : "text-xs py-1"
                )}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-0">
            {calendarDays.map((date, index) => {
              if (!date) {
                return <div key={`empty-${index}`} />
              }

              const dateEvents = getEventsForDate(date)
              const isToday = date.toDateString() === new Date().toDateString()
              const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString()
              const hasEvents = dateEvents.length > 0

              return (
                <motion.button
                  key={date.toISOString()}
                  whileHover={{ scale: interactive ? 1.1 : 1 }}
                  whileTap={{ scale: interactive ? 0.95 : 1 }}
                  className={cn(
                    "relative border border-transparent rounded-md transition-all",
                    compact ? "h-5 text-[10px]" : "h-10 text-sm",
                    isToday && "bg-primary/10 font-semibold",
                    isSelected && "border-primary bg-primary/5",
                    interactive && "hover:bg-muted/50 cursor-pointer",
                    !interactive && "cursor-default"
                  )}
                  onClick={() => {
                    if (interactive) {
                      setSelectedDate(date)
                      onDateClick?.(date, dateEvents)
                    }
                  }}
                >
                  <span className={cn(
                    isToday && "text-primary"
                  )}>
                    {date.getDate()}
                  </span>
                  
                  {/* Event indicators */}
                  {hasEvents && !compact && (
                    <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 flex gap-0.5">
                      {dateEvents.slice(0, 3).map((event, i) => (
                        <span
                          key={i}
                          className={cn(
                            "h-1 w-1 rounded-full",
                            getEventColor(event.type)
                          )}
                        />
                      ))}
                    </div>
                  )}
                  
                  {/* Compact mode single indicator */}
                  {hasEvents && compact && (
                    <span className={cn(
                      "absolute top-0.5 right-0.5 h-1 w-1 rounded-full",
                      getEventColor(dateEvents[0].type)
                    )} />
                  )}
                </motion.button>
              )
            })}
          </div>

          {/* Selected date events */}
          {selectedDate && !compact && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 p-2 bg-muted/50 rounded-lg"
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold">
                  {selectedDate.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </h4>
                {getEventsForDate(selectedDate).length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {getEventsForDate(selectedDate).length} events
                  </Badge>
                )}
              </div>
              
              {getEventsForDate(selectedDate).length === 0 ? (
                <p className="text-xs text-muted-foreground">No events scheduled</p>
              ) : (
                <div className="space-y-1 max-h-20 overflow-y-auto">
                  {getEventsForDate(selectedDate).map((event) => (
                    <motion.div
                      key={event.id}
                      whileHover={{ x: 2 }}
                      className="flex items-center gap-2 p-1 rounded cursor-pointer hover:bg-background/50"
                      onClick={() => onEventClick?.(event)}
                    >
                      <span className={cn(
                        "h-2 w-2 rounded-full flex-shrink-0",
                        getEventColor(event.type)
                      )} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{event.title}</p>
                        {event.time && (
                          <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                            <Clock className="h-2.5 w-2.5" />
                            {event.time}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Upcoming events (compact mode) */}
          {compact && events.length > 0 && (
            <div className="mt-2 space-y-0.5">
              <p className="text-[9px] font-medium text-muted-foreground mb-1">Upcoming</p>
              {events
                .filter(e => new Date(e.date) >= new Date())
                .slice(0, 2)
                .map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center gap-1 text-[10px] cursor-pointer hover:bg-muted/50 rounded px-1"
                    onClick={() => onEventClick?.(event)}
                  >
                    <span className={cn(
                      "h-1.5 w-1.5 rounded-full",
                      getEventColor(event.type)
                    )} />
                    <span className="truncate flex-1">{event.title}</span>
                    <span className="text-muted-foreground">
                      {new Date(event.date).getDate()}/{new Date(event.date).getMonth() + 1}
                    </span>
                  </div>
                ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export default CalendarWidget